import { useCallback } from 'react';
import useApi from './useApi';
import { useAppSettings } from '../hooks/useAppSettings';
import { CoatingNote } from './useGetCoatingNotes';

export interface CoatingNoteUpdateResponse {
    success: boolean;
    data: any;
    message: string;
}

/**
 * Custom hook for updating coating note data
 * Uses POST endpoint: /UpsertCoatingNote
 */
const useUpdateCoatingNote = () => {
    const { state, postApiData } = useApi();
    const { settings: appSettings } = useAppSettings();

    /**
     * Updates a coating note via the UpsertCoatingNote API endpoint
     * @param coatingNote - Complete coating note data
     * @returns Promise<CoatingNoteUpdateResponse>
     */
    const updateCoatingNote = useCallback(
        async (coatingNote: CoatingNote): Promise<CoatingNoteUpdateResponse> => {
            if (!coatingNote || coatingNote.id === undefined || coatingNote.id === null) {
                throw new Error('Coating note data with valid ID is required');
            }

            try {
                const apiUrl = appSettings?.api;
                if (!apiUrl) {
                    throw new Error('API URL not configured in app settings');
                }

                const endpoint = `${apiUrl}UpsertCoatingNote`;

                // Convert camelCase to PascalCase for API
                const apiPayload = {
                    Id: coatingNote.id,
                    Manufacturer: coatingNote.manufacturerId ? { Id: coatingNote.manufacturerId } : null,
                    IsGeneralNote: coatingNote.isGeneralNote,
                    Number: coatingNote.number,
                    Text: coatingNote.text,
                    FilePath: coatingNote.filePath,
                    LastUpdatedUtc: new Date().toISOString(),
                };

                const response = await postApiData({
                    endpoint,
                    data: apiPayload,
                    config: { params: { releaseId: appSettings?.releaseId || '' } },
                });

                return {
                    success: true,
                    data: response,
                    message: 'Coating note updated successfully',
                };
            } catch (error: any) {
                console.error('Failed to update coating note:', error);

                const errorMessage = error.response?.data?.message || error.response?.statusText || error.message || 'Failed to update coating note';

                throw new Error(errorMessage);
            }
        },
        [appSettings, postApiData]
    );

    return {
        updateCoatingNote,
        isLoading: state.isLoading,
        error: state.error,
    };
};

export default useUpdateCoatingNote;
